import type {Hotel} from '@/redux/v1/hotels/list/models';
import type {AsyncStatus} from '@react-hookz/web';

import {memo} from 'react';
import {useTranslation} from 'react-i18next';

import {HotelBoxHeader} from '@/components/HotelBoxHeader';
import {HotelBoxPlaceholder} from '@/components/HotelBoxPlaceholder';
import {useLinkBuilder} from '@/modules/common/hooks/useLinkBuilder';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {HotelBox} from '@/modules/hotels/components/List/components/HotelBox';
import {Container} from '@/modules/hotels/components/PromotedHotel/components/Container';

type Props = {
  id: string;
  hotel: Hotel | null;
  status: AsyncStatus;
  isVisuallyHidden: boolean;
  small?: boolean;
};

export const PromotedHotel: React.FunctionComponent<Props> = memo(
  function PromotedHotel({
    id: hotelId,
    hotel,
    status,
    isVisuallyHidden,
    small = false,
  }) {
    const [searchParams] = useSearchParams();
    const {t} = useTranslation('hotels');
    const {buildOfferListLink} = useLinkBuilder();

    if (status === 'error') {
      return null;
    }

    if (status === 'not-executed' || status === 'loading') {
      return (
        <Container hidden={isVisuallyHidden}>
          <HotelBoxPlaceholder small={small} />
        </Container>
      );
    }

    const link = buildOfferListLink({
      hotelId,
      getSortParams: () => null,
    });

    return (
      <Container hidden={isVisuallyHidden}>
        <HotelBoxHeader>{t('promotedHotel.headline')}</HotelBoxHeader>
        <HotelBox
          small={small}
          hotelId={hotelId}
          link={link}
          offer={hotel}
          position={-1}
          rooms={searchParams.rooms}
          travelType={searchParams.travelType}
        />
      </Container>
    );
  }
);
